import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext.jsx';
import api from '../services/api.js';

const JurisdictionContext = createContext(null);

/**
 * The signed-in session's jurisdiction pack — every vocabulary, unit, label
 * and map default the UI renders from.
 *
 * It arrives WITH the user (login and /me both carry it) rather than being
 * fetched separately, so there is never a moment where the app has a session
 * but no vocabulary to describe its data. That moment would render every
 * select empty and every extent as "NaN Acres" for one frame, which reads as a
 * bug to anyone watching a demo.
 */
export function JurisdictionProvider({ children }) {
  const { user } = useAuth();

  return (
    <JurisdictionContext.Provider value={user?.jurisdiction ?? null}>
      {children}
    </JurisdictionContext.Provider>
  );
}

/**
 * The current pack. Throws outside a signed-in session, because a component
 * that renders market vocabulary has nothing sensible to show without one —
 * far better to fail loudly in development than to silently render an
 * India-shaped form to a Dubai user.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useJurisdiction() {
  const pack = useContext(JurisdictionContext);

  if (!pack) {
    throw new Error(
      'useJurisdiction requires a signed-in session inside <JurisdictionProvider>. ' +
        'A public screen (the QR landing) should read its pack from the API payload instead.'
    );
  }

  return pack;
}

/** The pack when there is one, or null — for components that render either way. */
// eslint-disable-next-line react-refresh/only-export-components
export function useJurisdictionOrNull() {
  return useContext(JurisdictionContext);
}

/**
 * A pack for a screen that may have NO session.
 *
 * Parcel capture is open to anonymous Contacts — that is the point of the
 * capture link — so it cannot depend on a signed-in pack. When there is a
 * session it uses it; otherwise it fetches the market named in the URL
 * (`?jurisdiction=AE`) or the deployment default.
 *
 * Returns `{ pack, loading, error }` so the page can hold its render until the
 * vocabulary is in hand, rather than flashing an empty form.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useResolvedJurisdiction(requestedCode = null) {
  const session = useContext(JurisdictionContext);

  const [pack, setPack] = useState(session);
  const [loading, setLoading] = useState(!session);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (session) {
      setPack(session);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    const resolveCode = requestedCode
      ? Promise.resolve(requestedCode)
      : api.jurisdictions().then((data) => data.default);

    resolveCode
      .then((code) => api.jurisdictionPack(code))
      .then((fetched) => {
        if (cancelled) return;
        setPack(fetched);
        setError(null);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [session, requestedCode]);

  return { pack, loading, error };
}

export default JurisdictionContext;
